/**
 * All Inventory Report Export
 * Covers all 6 types in order: Products → Chemicals → Caps → Bottles → Labels → Supplies
 * Each type rendered as its own section with a divider header.
 * Summary view: aggregate rows.  Detailed view: + lot/batch breakdown rows.
 */

import { supabase } from '../../../../../supabaseClient';

// ── Styles ────────────────────────────────────────────────────────────────────

const getReportStyles = () => `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Helvetica', 'Arial', sans-serif; padding: 20px; font-size: 10px; line-height: 1.4; }
  .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
  .company-name { font-size: 16px; font-weight: bold; color: #333; }
  .report-title { font-size: 14px; color: #666; margin-top: 2px; }
  .report-date { font-size: 8px; color: #999; margin-top: 2px; }
  .stats { display: flex; justify-content: space-around; margin: 15px 0; gap: 10px; }
  .stat-card { flex: 1; text-align: center; padding: 8px; border-radius: 4px; border: 1px solid #ddd; }
  .stat-card.total    { background: #e3f2fd; border-color: #1976d2; }
  .stat-card.in-stock { background: #e8f5e9; border-color: #388e3c; }
  .stat-card.low-stock{ background: #fff3e0; border-color: #f57c00; }
  .stat-card.out-stock{ background: #ffebee; border-color: #d32f2f; }
  .stat-number { font-size: 16px; font-weight: bold; margin-bottom: 2px; }
  .stat-number.total    { color: #1976d2; }
  .stat-number.in-stock { color: #388e3c; }
  .stat-number.low-stock{ color: #f57c00; }
  .stat-number.out-stock{ color: #d32f2f; }
  .stat-label { font-size: 8px; text-transform: uppercase; font-weight: bold; }
  .section-header { background: #37474f; color: #fff; font-size: 10px; font-weight: bold;
                    text-transform: uppercase; letter-spacing: 0.5px; padding: 6px 10px;
                    margin-top: 20px; }
  .section-header:first-of-type { margin-top: 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 9px; }
  th, td { padding: 5px 7px; text-align: left; border-bottom: 1px solid #ddd; vertical-align: top; }
  th { background-color: #f0f0f0; font-weight: bold; font-size: 8px; text-transform: uppercase; text-align: center; }
  tr:nth-child(even) { background-color: #f9f9f9; }
  .item-name { font-weight: bold; font-size: 9px; margin-bottom: 1px; }
  .item-details { font-size: 7px; color: #666; }
  .qty-main { font-weight: bold; font-size: 9px; }
  .qty-sub { font-size: 7.5px; color: #666; display: block; margin-top: 1px; }
  .center-align { text-align: center; }
  .status { padding: 1px 3px; border-radius: 2px; font-size: 7px; font-weight: bold;
            text-transform: uppercase; text-align: center; display: inline-block; min-width: 50px; }
  .status.in-stock    { background: #d4edda; color: #155724; }
  .status.low-stock   { background: #fff3cd; color: #856404; }
  .status.out-of-stock{ background: #f8d7da; color: #721c24; }
  .lot-row { background-color: #f5f5f5 !important; font-size: 8px; }
  .lot-indent { padding-left: 18px; font-size: 8px; color: #555; }
  .expiry-warn { color: #c62828; font-weight: bold; }
  .expiry-soon { color: #e65100; }
  @page { margin: 0; size: letter landscape; }
  @media print {
    body { margin: 0; padding: 10px; }
    tr { page-break-inside: avoid; }
    table { page-break-inside: auto; }
    .section-header { page-break-before: auto; }
  }
`;

// ── Constants ─────────────────────────────────────────────────────────────────

const SECTION_ORDER = ['products', 'chemicals', 'caps', 'bottles', 'labels', 'supplies'];

const SECTION_LABELS = {
  products:  'Products',
  chemicals: 'Chemicals',
  caps:      'Caps',
  bottles:   'Bottles',
  labels:    'Labels',
  supplies:  'Supplies',
};

const SINGULAR_LABELS = {
  caps:     'Cap',
  bottles:  'Bottle',
  labels:   'Label',
  supplies: 'Supply',
};

// ── Shared helpers ────────────────────────────────────────────────────────────

const formatExpiry = (dateStr) => {
  if (!dateStr) return '-';
  const [y, m, d] = dateStr.split('-').map(Number);
  const date  = new Date(y, m - 1, d);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const in30  = new Date(today); in30.setDate(today.getDate() + 30);
  const label = date.toLocaleDateString();
  if (date <= today) return `<span class="expiry-warn">${label} (Expired)</span>`;
  if (date < in30)   return `<span class="expiry-soon">${label} (Soon)</span>`;
  return label;
};

const statusOf = (qty, reorderPoint) => {
  if (qty <= 0)                           return { label: 'Out of Stock', cls: 'out-of-stock' };
  if (reorderPoint > 0 && qty <= reorderPoint) return { label: 'Low Stock',    cls: 'low-stock' };
  return                                        { label: 'In Stock',     cls: 'in-stock' };
};

const lotLabel = (lot) =>
  [lot.lot_number   ? `Lot: ${lot.lot_number}`     : null,
   lot.batch_number ? `Batch: ${lot.batch_number}` : null]
    .filter(Boolean).join('  ') || '(no lot/batch)';

// ── Stats across ALL types ────────────────────────────────────────────────────

const calcAllStats = (byType) => {
  let totalItems = 0, inStock = 0, lowStock = 0, outOfStock = 0;

  SECTION_ORDER.forEach(type => {
    const items = byType[type] || [];
    items.forEach(item => {
      totalItems++;
      const qty = type === 'chemicals'
        ? (item.total_quantity || 0)
        : (item.total_base_units || item.total_quantity || 0);
      const rp = item.reorder_point || 0;
      if (qty <= 0) outOfStock++;
      else if (rp > 0 && qty <= rp) lowStock++;
      else inStock++;
    });
  });

  return { totalItems, inStock, lowStock, outOfStock };
};

const statsBlock = (stats) => `
  <div class="stats">
    <div class="stat-card total">
      <div class="stat-number total">${stats.totalItems.toLocaleString()}</div>
      <div class="stat-label">Total Items</div>
    </div>
    <div class="stat-card in-stock">
      <div class="stat-number in-stock">${stats.inStock.toLocaleString()}</div>
      <div class="stat-label">In Stock</div>
    </div>
    <div class="stat-card low-stock">
      <div class="stat-number low-stock">${stats.lowStock.toLocaleString()}</div>
      <div class="stat-label">Low Stock</div>
    </div>
    <div class="stat-card out-stock">
      <div class="stat-number out-stock">${stats.outOfStock.toLocaleString()}</div>
      <div class="stat-label">Out of Stock</div>
    </div>
  </div>
`;

// ── Products section ──────────────────────────────────────────────────────────

const computeProductCases = (item) => {
  const lots         = item.lots || [];
  const totalBase    = item.total_base_units || item.total_quantity || 0;
  const unitsPerCase = item.units_per_case || 1;
  if (lots.length > 0) {
    return lots.reduce((sum, lot) => {
      const upc = lot.units_per_case || unitsPerCase;
      return sum + Math.floor((lot.total_base_units || 0) / upc);
    }, 0);
  }
  return Math.floor(totalBase / unitsPerCase);
};

const productSummaryRows = (items) => {
  const sorted = [...items].sort((a, b) => {
    const na = (a.item_name || '').toLowerCase();
    const nb = (b.item_name || '').toLowerCase();
    if (na !== nb) return na.localeCompare(nb);
    const sa = parseFloat((a.variant_name || '').match(/(\d+(\.\d+)?)/)?.[1] || 0);
    const sb = parseFloat((b.variant_name || '').match(/(\d+(\.\d+)?)/)?.[1] || 0);
    return sb - sa;
  });

  return sorted.map(item => {
    const totalBase = item.total_base_units || item.total_quantity || 0;
    const cases     = computeProductCases(item);
    const rp        = item.reorder_point > 0 ? `${item.reorder_point.toLocaleString()} units` : '-';
    const st        = statusOf(totalBase, item.reorder_point || 0);
    return `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.variant_name || '-'}</td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align">
          <span class="qty-main">${cases.toLocaleString()} cases</span>
          <span class="qty-sub">${totalBase.toLocaleString()} total units</span>
        </td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${st.cls}">${st.label}</span></td>
      </tr>`;
  }).join('');
};

const productDetailedRows = (items) => {
  const sorted = [...items].sort((a, b) => {
    const na = (a.item_name || '').toLowerCase();
    const nb = (b.item_name || '').toLowerCase();
    if (na !== nb) return na.localeCompare(nb);
    const sa = parseFloat((a.variant_name || '').match(/(\d+(\.\d+)?)/)?.[1] || 0);
    const sb = parseFloat((b.variant_name || '').match(/(\d+(\.\d+)?)/)?.[1] || 0);
    return sb - sa;
  });

  return sorted.map(item => {
    const totalBase    = item.total_base_units || item.total_quantity || 0;
    const cases        = computeProductCases(item);
    const rp           = item.reorder_point > 0 ? `${item.reorder_point.toLocaleString()} units` : '-';
    const st           = statusOf(totalBase, item.reorder_point || 0);
    const unitsPerCase = item.units_per_case || 1;
    const activeLots   = (item.lots || []).filter(l => l.is_active && (l.total_base_units || 0) > 0);

    const header = `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.variant_name || '-'}</td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align"><strong>${activeLots.length}</strong></td>
        <td class="center-align">
          <span class="qty-main">${cases.toLocaleString()} cases</span>
          <span class="qty-sub">${totalBase.toLocaleString()} total units</span>
        </td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${st.cls}">${st.label}</span></td>
      </tr>`;

    const lots = activeLots.map(lot => {
      const upc      = lot.units_per_case || unitsPerCase;
      const lotBase  = lot.total_base_units || 0;
      const lotCases = Math.floor(lotBase / upc);
      const mfg      = lot.manufactured_date
        ? new Date(lot.manufactured_date + 'T00:00:00').toLocaleDateString()
        : null;
      return `
        <tr class="lot-row">
          <td class="lot-indent">${lotLabel(lot)}</td>
          <td></td><td></td><td></td>
          <td class="center-align">
            <span class="qty-main">${lotCases.toLocaleString()} cases</span>
            <span class="qty-sub">${lotBase.toLocaleString()} units</span>
          </td>
          <td class="center-align">
            ${mfg ? `Mfg: ${mfg}<br/>` : ''}Exp: ${formatExpiry(lot.expiry_date)}
          </td>
          <td></td>
        </tr>`;
    }).join('');

    return header + lots;
  }).join('');
};

const productSectionSummary = (items) => `
  <div class="section-header">${SECTION_LABELS.products} (${items.length})</div>
  <table>
    <thead><tr>
      <th width="22%" style="text-align:left">Product</th>
      <th width="13%">Variant</th>
      <th width="13%">Location</th>
      <th width="20%">Cases</th>
      <th width="16%">Reorder Point</th>
      <th width="16%">Status</th>
    </tr></thead>
    <tbody>${productSummaryRows(items)}</tbody>
  </table>`;

const productSectionDetailed = (items) => `
  <div class="section-header">${SECTION_LABELS.products} (${items.length})</div>
  <table>
    <thead><tr>
      <th width="20%" style="text-align:left">Product</th>
      <th width="11%">Variant</th>
      <th width="10%">Location</th>
      <th width="5%">Lots</th>
      <th width="20%">Cases</th>
      <th width="18%">Dates</th>
      <th width="16%">Status</th>
    </tr></thead>
    <tbody>${productDetailedRows(items)}</tbody>
  </table>`;

// ── Chemicals section ─────────────────────────────────────────────────────────

const chemicalSummaryRows = (items) =>
  items.map(item => {
    const qty    = item.total_quantity || 0;
    const base   = item.total_base_quantity || 0;
    const uType  = item.unit_type    || 'units';
    const uMeas  = item.unit_measure || '';
    const rp     = item.reorder_point    ? `${item.reorder_point.toLocaleString()} ${uType}`    : '-';
    const rq     = item.reorder_quantity ? `${item.reorder_quantity.toLocaleString()} ${uType}` : '-';
    const st     = statusOf(qty, item.reorder_point || 0);
    const stock  = uMeas
      ? `<span class="qty-main">${qty.toLocaleString()} ${uType}</span><br/><span class="qty-sub">${base.toLocaleString()} ${uMeas}</span>`
      : `<span class="qty-main">${qty.toLocaleString()} ${uType}</span>`;
    return `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align">${uType} / ${uMeas || '-'}</td>
        <td class="center-align">${stock}</td>
        <td class="center-align">${rp}</td>
        <td class="center-align">${rq}</td>
        <td class="center-align"><span class="status ${st.cls}">${st.label}</span></td>
      </tr>`;
  }).join('');

const chemicalDetailedRows = (items) =>
  items.map(item => {
    const qty       = item.total_quantity || 0;
    const base      = item.total_base_quantity || 0;
    const uType     = item.unit_type    || 'units';
    const uMeas     = item.unit_measure || '';
    const rp        = item.reorder_point ? `${item.reorder_point.toLocaleString()} ${uType}` : '-';
    const st        = statusOf(qty, item.reorder_point || 0);
    const stock     = uMeas
      ? `<span class="qty-main">${qty.toLocaleString()} ${uType}</span><br/><span class="qty-sub">${base.toLocaleString()} ${uMeas}</span>`
      : `<span class="qty-main">${qty.toLocaleString()} ${uType}</span>`;
    const activeLots = (item.lots || []).filter(l => l.is_active && (l.current_unit_quantity || 0) > 0);

    const header = `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align">${uType} / ${uMeas || '-'}</td>
        <td class="center-align"><strong>${activeLots.length}</strong></td>
        <td class="center-align">${stock}</td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${st.cls}">${st.label}</span></td>
      </tr>`;

    const lots = activeLots.map(lot => {
      const lotUnit = lot.current_unit_quantity  || 0;
      const lotBase = lot.current_total_quantity || 0;
      const qtyStr  = uMeas
        ? `${lotUnit.toLocaleString()} ${uType} / ${lotBase.toLocaleString()} ${uMeas}`
        : `${lotUnit.toLocaleString()} ${uType}`;
      return `
        <tr class="lot-row">
          <td class="lot-indent">${lotLabel(lot)}</td>
          <td></td><td></td>
          <td></td>
          <td class="center-align">${qtyStr}</td>
          <td class="center-align">${formatExpiry(lot.expiry_date)}</td>
          <td></td>
        </tr>`;
    }).join('');

    return header + lots;
  }).join('');

const chemicalSectionSummary = (items) => `
  <div class="section-header">${SECTION_LABELS.chemicals} (${items.length})</div>
  <table>
    <thead><tr>
      <th width="22%" style="text-align:left">Chemical</th>
      <th width="11%">Location</th>
      <th width="11%">Unit / Measure</th>
      <th width="17%">Stock</th>
      <th width="13%">Reorder Point</th>
      <th width="13%">Reorder Qty</th>
      <th width="13%">Status</th>
    </tr></thead>
    <tbody>${chemicalSummaryRows(items)}</tbody>
  </table>`;

const chemicalSectionDetailed = (items) => `
  <div class="section-header">${SECTION_LABELS.chemicals} (${items.length})</div>
  <table>
    <thead><tr>
      <th width="20%" style="text-align:left">Chemical</th>
      <th width="10%">Location</th>
      <th width="10%">Unit / Measure</th>
      <th width="5%">Lots</th>
      <th width="18%">Quantity</th>
      <th width="13%">Reorder Point</th>
      <th width="24%">Status</th>
    </tr></thead>
    <tbody>${chemicalDetailedRows(items)}</tbody>
  </table>`;

// ── Generic section (Caps / Bottles / Labels / Supplies) ──────────────────────

const genericSummaryRows = (items) =>
  items.map(item => {
    const qty = item.total_base_units || item.total_quantity || 0;
    const rp  = item.reorder_point > 0 ? item.reorder_point.toLocaleString() : '-';
    const st  = statusOf(qty, item.reorder_point || 0);
    return `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align"><span class="qty-main">${qty.toLocaleString()}</span></td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${st.cls}">${st.label}</span></td>
      </tr>`;
  }).join('');

const genericDetailedRows = (items) =>
  items.map(item => {
    const qty        = item.total_base_units || item.total_quantity || 0;
    const rp         = item.reorder_point > 0 ? item.reorder_point.toLocaleString() : '-';
    const st         = statusOf(qty, item.reorder_point || 0);
    const activeLots = (item.lots || []).filter(l => l.is_active && (l.total_base_units || l.current_quantity || 0) > 0);

    const header = `
      <tr>
        <td>
          <div class="item-name">${item.item_name || 'Unknown'}</div>
          ${item.item_sku ? `<div class="item-details">SKU: ${item.item_sku}</div>` : ''}
        </td>
        <td class="center-align">${item.location_name || '-'}</td>
        <td class="center-align"><strong>${activeLots.length}</strong></td>
        <td class="center-align"><span class="qty-main">${qty.toLocaleString()}</span></td>
        <td class="center-align">${rp}</td>
        <td class="center-align"><span class="status ${st.cls}">${st.label}</span></td>
      </tr>`;

    const lots = activeLots.map(lot => {
      const lotQty = lot.total_base_units || lot.current_quantity || 0;
      return `
        <tr class="lot-row">
          <td class="lot-indent">${lotLabel(lot)}</td>
          <td></td>
          <td></td>
          <td class="center-align">${lotQty.toLocaleString()}</td>
          <td class="center-align">${formatExpiry(lot.expiry_date)}</td>
          <td></td>
        </tr>`;
    }).join('');

    return header + lots;
  }).join('');

const genericSectionSummary = (type, items) => `
  <div class="section-header">${SECTION_LABELS[type]} (${items.length})</div>
  <table>
    <thead><tr>
      <th width="30%" style="text-align:left">${SINGULAR_LABELS[type] || SECTION_LABELS[type]}</th>
      <th width="20%">Location</th>
      <th width="18%">Quantity</th>
      <th width="16%">Reorder Point</th>
      <th width="16%">Status</th>
    </tr></thead>
    <tbody>${genericSummaryRows(items)}</tbody>
  </table>`;

const genericSectionDetailed = (type, items) => `
  <div class="section-header">${SECTION_LABELS[type]} (${items.length})</div>
  <table>
    <thead><tr>
      <th width="28%" style="text-align:left">${SINGULAR_LABELS[type] || SECTION_LABELS[type]}</th>
      <th width="16%">Location</th>
      <th width="6%">Lots</th>
      <th width="18%">Quantity</th>
      <th width="16%">Reorder Point</th>
      <th width="16%">Status</th>
    </tr></thead>
    <tbody>${genericDetailedRows(items)}</tbody>
  </table>`;

// ── Document builders ─────────────────────────────────────────────────────────

const generateAllSummary = (companyName, byType) => {
  const stats    = calcAllStats(byType);
  const sections = SECTION_ORDER.map(type => {
    const items = byType[type] || [];
    if (!items.length) return `<div class="section-header">${SECTION_LABELS[type]} (0)</div><p style="font-size:8px;color:#999;padding:6px 10px;margin:0;">No items.</p>`;
    if (type === 'products')  return productSectionSummary(items);
    if (type === 'chemicals') return chemicalSectionSummary(items);
    return genericSectionSummary(type, items);
  }).join('');

  return `<!DOCTYPE html><html>
    <head><title>All Inventory Report — Summary</title><style>${getReportStyles()}</style></head>
    <body>
      <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="report-title">All Inventory Report — Summary</div>
        <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      ${statsBlock(stats)}
      ${sections}
    </body>
  </html>`;
};

const generateAllDetailed = (companyName, byType) => {
  const stats    = calcAllStats(byType);
  const sections = SECTION_ORDER.map(type => {
    const items = byType[type] || [];
    if (!items.length) return `<div class="section-header">${SECTION_LABELS[type]} (0)</div><p style="font-size:8px;color:#999;padding:6px 10px;margin:0;">No items.</p>`;
    if (type === 'products')  return productSectionDetailed(items);
    if (type === 'chemicals') return chemicalSectionDetailed(items);
    return genericSectionDetailed(type, items);
  }).join('');

  return `<!DOCTYPE html><html>
    <head><title>All Inventory Report — Detailed</title><style>${getReportStyles()}</style></head>
    <body>
      <div class="header">
        <div class="company-name">${companyName}</div>
        <div class="report-title">All Inventory Report — Detailed (Lot Breakdown)</div>
        <div class="report-date">Generated: ${new Date().toLocaleString()}</div>
      </div>
      ${statsBlock(stats)}
      ${sections}
    </body>
  </html>`;
};

// ── Public export ─────────────────────────────────────────────────────────────

/**
 * @param {string}  companyId
 * @param {object}  byType  — { products: [...], chemicals: [...], caps: [...], bottles: [...], labels: [...], supplies: [...] }
 * @param {'summary'|'detailed'} reportType
 */
export const exportAllInventoryReport = async (companyId, byType, reportType = 'summary') => {
  let companyName = 'Company';
  try {
    const { data } = await supabase.from('companies').select('name').eq('id', companyId).single();
    if (data) companyName = data.name;
  } catch (_) {}

  const html = reportType === 'detailed'
    ? generateAllDetailed(companyName, byType)
    : generateAllSummary(companyName, byType);

  const blob   = new Blob([html], { type: 'text/html' });
  const url    = URL.createObjectURL(blob);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;';
  iframe.src = url;
  document.body.appendChild(iframe);

  iframe.onload = () => {
    iframe.contentWindow.focus();
    iframe.contentWindow.print();
    setTimeout(() => {
      document.body.removeChild(iframe);
      URL.revokeObjectURL(url);
    }, 1000);
  };
};
